import { useEffect, useState } from 'react'

import { amount, isAmountString } from '@/shared/lib'

/**
 * Which way a refreshed price just moved — `'up'`, `'down'`, or `null` once
 * the moment has passed.
 *
 * The coin prices on the trade form and the price alert refresh on an
 * interval, and a figure that changes in place is easy to miss. This lets the
 * caller tint it for a beat in the direction it moved, then settle back.
 *
 * `key` names what the price belongs to — the market. A new key is a different
 * price, not a move, so it resets without a tick: switching from BTC to USDT
 * must not flash red because USDT is cheaper.
 */
const TICK_MS = 1200

export function usePriceTick(
  value: string | null | undefined,
  key: string,
): 'up' | 'down' | null {
  const [last, setLast] = useState({ key, value })
  const [tick, setTick] = useState<{ dir: 'up' | 'down'; at: string } | null>(null)

  // Adjusted during render, so the tint lands on the same paint as the figure.
  if (last.key !== key || last.value !== value) {
    setLast({ key, value })
    if (last.key !== key) {
      setTick(null)
    } else if (isAmountString(value) && isAmountString(last.value)) {
      const cmp = amount(value).comparedTo(amount(last.value))
      if (cmp !== 0) setTick({ dir: cmp > 0 ? 'up' : 'down', at: value })
    }
  }

  useEffect(() => {
    if (!tick) return
    const timer = setTimeout(() => setTick(null), TICK_MS)
    return () => clearTimeout(timer)
  }, [tick])

  return tick?.dir ?? null
}
